import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, ElementRef, ViewChild, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
// import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
// import { CompositeScoreWidgetComponent } from '../../components/composite-score-widget/composite-score-widget.component';
// import { CommonPerformanceTableComponent } from '../../components/common-performance-table/common-performance-table.component';
// import { CitationCardComponent } from '../../components/citation-card/citation-card.component';
// import { GenericAiVisibilityComponent } from '../../components/generic-ai-visibility/generic-ai-visibility.component';
import { BarChartComponent } from '../../charts/bar-chart/bar-chart.component';
import { PieChartComponent } from '../../charts/pie-chart/pie-chart.component';
import { CommonSimpleTableComponent, TableColumn, TableConfig } from '../../components/common-simple-table/common-simple-table.component';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { GenericCommonTabsComponent } from '../../components/template-ai-common/generic-common-tabs/generic-common-tabs.component';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { TaskStatus } from '../../models/bulk-search.models';

@Component({
  selector: 'app-generic-ai-citations',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    // CitationCardComponent,
    // GenericAiVisibilityComponent,
    // BarLineComboChartComponent,
    // CompositeScoreWidgetComponent,
    // CommonPerformanceTableComponent,
    BarChartComponent,
    PieChartComponent,
    CommonSimpleTableComponent,
  ],
  templateUrl: './generic-ai-citations.component.html',
  styleUrl: './generic-ai-citations.component.scss'
})
export class GenericAiCitationsComponent implements OnInit, OnDestroy {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  // Core data properties
  aiCitationsResponse: any = null;
  brands: any[] = [];
  metadata: any = null;

  // Visibility and memory management
  isTabVisible: boolean = false;
  isChartRendered: boolean = false;
  shouldRenderChart: boolean = false;

  // Chart properties
  results: any[] = [];
  lineChart: any[] = [];

  // Composite Score
  compositeScore: number = 0;

  // Color schemes for charts
  comboBarScheme: any = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'ordinal',
    domain: ['#1976d2']
  };

  lineChartScheme: any = {
    name: 'coolthree',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b', '#7aa3e5', '#a8385d', '#00bfa5']
  };

  // Responsive view dimensions
  view: [number, number] = [600, 400];

  // Table properties
  tableColumns: any[] = [];
  tableRows: any[] = [];

  // Evidence sources
  evidenceSources: any[] = [];

  // Selected domain for "cited pages for this domain" detail (Simple AI Cited section)
  selectedDomainForPages: string | null = null;
  // View state: main list vs domain-detail screen (with back button)
  aiCitationsView: 'main' | 'domain-detail' = 'main';

  // AI Visibility (same as Overview tab - uses llm_comparison data)
  llmComparisonMetricsProgress: TaskStatus | null = null;
  isLLMComparisonMetricsLoading = false;
  llmComparisonMetricsTaskId: string | null = null;
  /** When false, progress text below loader is hidden (set on reload click) */
  showProgressBelowLoader = true;

  readonly allPlatformIds: readonly string[] = ['chatgpt', 'ai_overview', 'ai_mode', 'gemini'];
  private readonly platformFilterAllId = 'all';

  platformFilterOptions: { id: string; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'chatgpt', label: 'Chat GPT' },
    { id: 'ai_overview', label: 'AI Overview' },
    { id: 'ai_mode', label: 'AI Mode' },
    { id: 'gemini', label: 'Gemini' }
  ];

  /** Selected real platforms only (never includes 'all'). */
  selectedPlatformIds: string[] = ['chatgpt', 'ai_overview', 'ai_mode', 'gemini'];

  /** Last mat-select model for All vs platform toggles. */
  private platformFilterPrevMatValue: string[] = [];

  get isPlatformFilterValid(): boolean {
    return (this.selectedPlatformIds?.length ?? 0) > 0;
  }

  /** Mat-select value: All + four ids when fully selected; else subset; [] when none. */
  get platformFilterMatSelectValue(): string[] {
    const ids = (this.selectedPlatformIds || []).filter((id) => this.allPlatformIds.includes(id));
    if (ids.length === 0) return [];
    if (this.allPlatformIds.every((p) => ids.includes(p))) {
      return [this.platformFilterAllId, ...this.allPlatformIds];
    }
    return [...ids];
  }

  /**
   * All ON → all platforms. All OFF (only token removed) → one platform (min).
   * Pick All with partial → all. At least one platform always remains selected.
   */
  onPlatformFilterChange(selected: string[]): void {
    const ALL = this.platformFilterAllId;
    const IDS = this.allPlatformIds;
    const next = [...(selected || [])];
    const prev = [...this.platformFilterPrevMatValue];
    const minOne = (): string[] => [IDS[0]];

    if (next.length === 1 && next[0] === ALL) {
      this.selectedPlatformIds = [...IDS];
    } else if (prev.includes(ALL) && !next.includes(ALL)) {
      const prevP = new Set(prev.filter((x) => x !== ALL && IDS.includes(x)));
      const nextP = new Set(next.filter((x) => x !== ALL && IDS.includes(x)));
      const samePlatforms =
        prevP.size === nextP.size && [...prevP].every((p) => nextP.has(p));
      if (nextP.size === 0 || samePlatforms) {
        this.selectedPlatformIds = minOne();
      } else {
        this.selectedPlatformIds = IDS.filter((id) => nextP.has(id));
      }
    } else if (!prev.includes(ALL) && next.includes(ALL)) {
      this.selectedPlatformIds = [...IDS];
    } else {
      this.selectedPlatformIds = IDS.filter((id) => next.includes(id));
    }

    if (this.selectedPlatformIds.length === 0) {
      this.selectedPlatformIds = minOne();
    }

    this.platformFilterPrevMatValue = [...this.platformFilterMatSelectValue];
    this.cdr.markForCheck();
  }

  getPlatformFilterDisplayLabel(): string {
    if (!this.isPlatformFilterValid) return this.platformFilterOptions.find((o) => o.id === this.allPlatformIds[0])?.label ?? 'Platform';
    if (this.selectedPlatformIds.length === this.allPlatformIds.length) return 'All platforms';
    const labels = this.selectedPlatformIds
      .map((id) => this.platformFilterOptions.find((o) => o.id === id)?.label)
      .filter(Boolean) as string[];
    return labels.length > 0 ? labels.join(', ') : 'Select platforms';
  }

  /** When invalid, no data; when all four selected, no narrowing; else filter to subset. */
  private get activePlatformFilterIds(): string[] {
    if (!this.isPlatformFilterValid) return [];
    return this.selectedPlatformIds.filter((id) => this.allPlatformIds.includes(id));
  }

  private get hasActivePlatformFilter(): boolean {
    return this.isPlatformFilterValid && this.activePlatformFilterIds.length < this.allPlatformIds.length;
  }

  // Cited domains table properties
  citedDomainsColumns: TableColumn[] = [
    { key: 'domain', label: 'Domain', sortable: true, width: '250px' },
    { key: 'citation_count', label: 'Citations', sortable: true, align: 'center', width: '100px' },
    { key: 'authority_score', label: 'Authority Score', sortable: true, align: 'center', width: '130px' },
    { key: 'sources', label: 'Sources', sortable: false, width: '200px' },
    { key: 'confidence_avg', label: 'Avg Confidence', sortable: true, align: 'center', width: '120px' }
  ];

  citedDomainsConfig: TableConfig = {
    showHeader: true,
    showCard: true,
    highlightTopRows: 3,
    enableSorting: false,
    enableHover: true,
    cardClass: '',
    tableClass: ''
  };

  // Recommended domains table properties
  recommendedDomainsColumns: TableColumn[] = [
    { key: 'domain', label: 'Domain', sortable: true, width: '250px' },
    { key: 'relevance_score', label: 'Relevance', sortable: true, align: 'center', width: '120px' },
    { key: 'category', label: 'Category', sortable: true, width: '150px' },
    { key: 'is_valid', label: 'Status', sortable: false, align: 'center', width: '100px' }
  ];

  recommendedDomainsConfig: TableConfig = {
    showHeader: true,
    showCard: true,
    highlightTopRows: 3,
    enableSorting: false,
    enableHover: true,
    cardClass: '',
    tableClass: ''
  };

  // Cited pages table properties
  citedPagesColumns: TableColumn[] = [
    { key: 'page', label: 'Page', sortable: true, width: '300px' },
    { key: 'ai_responses', label: 'AI Responses', sortable: true, align: 'center', width: '120px' },
    { key: 'volume', label: 'Volume', sortable: true, align: 'center', width: '100px' },
    { key: 'llm_sources', label: 'LLM Sources', sortable: false, width: '200px' },
    { key: 'from_prompts', label: 'From prompts · Mentions', sortable: false, width: '200px' },
    { key: 'domain', label: 'Domain', sortable: true, width: '200px' }
  ];

  citedPagesConfig: TableConfig = {
    showHeader: true,
    showCard: true,
    highlightTopRows: 5,
    enableSorting: true,
    enableHover: true,
    cardClass: '',
    tableClass: ''
  };

  // Filters for cited pages
  citedPagesFilters = {
    selectedLLMSources: [] as string[],
    selectedDomains: [] as string[],
    dateRange: { start: null as Date | null, end: null as Date | null }
  };

  // Smart date selection
  smartDateRange: { start: Date | null, end: Date | null } = { start: null, end: null };

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Category filter for cited domains
  citedDomainCategories: string[] = [
    'All',
    'Industry related',
    'News related domains',
    'Industry community',
    'Other'
  ];
  selectedCitedDomainCategory: string = 'All';


  // Event handlers for cited domains table
  onCitedDomainRowClick(row: any): void {
    // Custom logic for row click
  }

  onCitedDomainSortChange(event: {column: string, direction: 'asc' | 'desc'}): void {
    // Custom sorting logic if needed
  }

  get firstBrandName(): string {
    const youBrand = this.findYouBrand(this.brands || []);
    return youBrand ? youBrand.name : 'Your Brand';
  }

  // Get citations from AI Citations response
  getCitations(): any[] {
    if (!this.aiCitationsResponse) return [];
    
    // Try multiple locations for citations - they might be at different levels
    let citations: any[] = [];
    
    // First, try structured_data.context.citations (most common location)
    if (this.aiCitationsResponse.structured_data?.context?.citations) {
      citations = this.aiCitationsResponse.structured_data.context.citations;
    }
    // Try root level context.citations
    else if (this.aiCitationsResponse.context?.citations) {
      citations = this.aiCitationsResponse.context.citations;
    }
    // Try root level citations
    else if (this.aiCitationsResponse.citations) {
      citations = this.aiCitationsResponse.citations;
    }
    // Try per-brand citations and aggregate them (when competitors are present)
    else if (this.brands && this.brands.length > 0) {
      // Collect citations from all brands
      const allCitations: any[] = [];
      this.brands.forEach((brand: any) => {
        if (brand.citations && Array.isArray(brand.citations)) {
          allCitations.push(...brand.citations);
        }
        if (brand.context?.citations && Array.isArray(brand.context.citations)) {
          allCitations.push(...brand.context.citations);
        }
      });
      // Remove duplicates based on URL
      const uniqueCitations = allCitations.filter((citation, index, self) =>
        index === self.findIndex(c => c.url === citation.url)
      );
      citations = uniqueCitations;
    }
    
    return citations.length > 0 ? this.convertToCitations(citations) : [];
  }

  // Combo Chart Axis Scaling and Formatting - matching ngx-charts structure
  yLeftAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yRightAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yLeftTickFormat(data: number): string {
    return `${data.toLocaleString()}`;
  }

  yRightTickFormat(data: number): string {
    return `${data}%`;
  }

  // Chart event handlers
  onSelect(event: any): void {
    // Chart selection handler
  }

  onActivate(event: any): void {
    // Chart activation handler
  }

  onDeactivate(event: any): void {
    // Chart deactivation handler
  }

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    @Optional() @SkipSelf() private parentTabs?: GenericCommonTabsComponent
  ) {
    // Calculate smart date range on initialization
    this.calculateSmartDateRange();
  }

  /** LLM comparison data for AI Visibility (same source as Overview - llm_comparison.comparison_data) */
  get llmComparisonData(): any[] {
    const r = this.aiCitationsResponse;
    const llm = r?.llm_comparison || r?.overview?.llm_comparison || r?.structured_data?.llm_comparison
      || this.templateCommonService.getCurrentAnalysisResponse()?.llm_comparison;
    const list = llm?.comparison_data;
    return Array.isArray(list) ? list : [];
  }

  ngOnInit(): void {
    this.platformFilterPrevMatValue = [...this.platformFilterMatSelectValue];
    // Subscribe to AI citations response
    const analysisSub = this.templateCommonService.aiCitationsResponse$.subscribe((response: any) => {
      if (response) {
        this.aiCitationsResponse = response;
        this.processAICitationsData(response);
        this.cdr.detectChanges();
      }
    });

    // Subscribe to LLM comparison metrics progress (same as Overview)
    const llmProgressSub = this.templateCommonService.llmComparisonMetricsProgress$.subscribe((progress: TaskStatus | null) => {
      this.llmComparisonMetricsProgress = progress;
      if (progress) {
        this.isLLMComparisonMetricsLoading = progress.status === 'progress' || progress.status === 'pending' || progress.status === 'in_progress';
      } else if (!this.llmComparisonMetricsTaskId) {
        this.isLLMComparisonMetricsLoading = false;
      }
      this.cdr.detectChanges();
    });

    const llmTaskIdSub = this.templateCommonService.llmComparisonMetricsTaskId$.subscribe((taskId: string | null) => {
      this.llmComparisonMetricsTaskId = taskId;
      if (!taskId) this.showProgressBelowLoader = true; // Reset so progress shows on next run
      if (!taskId) {
        this.isLLMComparisonMetricsLoading = false;
      }
      this.cdr.detectChanges();
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub, llmProgressSub, llmTaskIdSub);

    // Load data when component initializes
    this.loadAICitationsData();

    // Check visibility after component initialization
    setTimeout(() => {
      this.checkTabVisibility();
    }, 100);

    // Update responsive view dimensions
    this.updateViewByBreakpoint();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateViewByBreakpoint, { passive: true });
    }
  }

  ngOnDestroy(): void {
    console.log('🗑️ AI Citations: Destroying component and clearing memory...');
    
    // Clear chart data to free memory
    this.clearChartData();
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Don't clear service-level cache - data should persist across tab switches
    // The service manages the data and tabs subscribe to observables
    
    // Clear all local data references
    this.aiCitationsResponse = null;
    this.brands = [];
    this.metadata = null;
    
    // Reset visibility flags
    this.isTabVisible = false;
    this.isChartRendered = false;
    this.shouldRenderChart = false;
    
    console.log('✅ AI Citations: Component destroyed and memory cleared');
  }

  /**
   * Clear chart data to reduce memory usage
   */
  private clearChartData(): void {
    this.results = [];
    this.lineChart = [];
    this.isChartRendered = false;
    this.shouldRenderChart = false;
  }

  /**
   * Check if tab is visible using Intersection Observer
   */
  @HostListener('window:scroll')
  onScroll(): void {
    this.checkTabVisibility();
  }

  /**
   * Check tab visibility and manage chart rendering
   */
  private checkTabVisibility(): void {
    if (!this.elementRef?.nativeElement) return;
    
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
    
    if (isVisible !== this.isTabVisible) {
      this.isTabVisible = isVisible;
      console.log(`🔍 AI Citations: Tab visibility changed to ${isVisible}`);
      
      if (isVisible) {
        this.onTabBecameVisible();
      } else {
        this.onTabBecameHidden();
      }
    }
  }

  /**
   * Handle tab becoming visible
   */
  private onTabBecameVisible(): void {
    console.log('👁️ AI Citations: Tab became visible, rendering chart...');
    this.shouldRenderChart = true;
    this.renderChartIfNeeded();
  }

  /**
   * Handle tab becoming hidden
   */
  private onTabBecameHidden(): void {
    console.log('🙈 AI Citations: Tab became hidden, clearing chart...');
    this.shouldRenderChart = false;
    this.clearChartData();
    this.cdr.detectChanges();
  }

  /**
   * Render chart only if tab is visible and data is available
   */
  private renderChartIfNeeded(): void {
    if (!this.shouldRenderChart || this.isChartRendered) return;
    
    if (this.brands && this.brands.length > 0) {
      console.log('📊 AI Citations: Rendering chart with data...');
      this.prepareChartData();
      this.isChartRendered = true;
      this.cdr.detectChanges();
    }
  }


  loadAICitationsData(): void {
    // Check if we already have a response (data might have been loaded before component initialized)
    const currentResponse = this.templateCommonService.getCurrentAiCitationsResponse();
    if (currentResponse) {
      console.log('AI Citations: Found existing response, processing immediately');
      this.aiCitationsResponse = currentResponse;
      this.processAICitationsData(currentResponse);
      this.cdr.detectChanges();
    } else {
      // No data yet - subscription in ngOnInit() will handle it when data arrives
      console.log('AI Citations: No data yet, waiting for subscription to receive data');
    }
  }

  // Method to trigger reload from parent component
  onReload(): void {
    this.templateCommonService.triggerTabReload('ai-citations', true);
  }

  // Method to retry loading data (used in error state)
  retryLoad(): void {
    this.onReload();
  }

  private processAICitationsData(data: any): void {
    console.log('🔍 AI Citations: Processing data:', data);
    
    this.aiCitationsResponse = data;

    // Extract structured_data from response
    const processedData = data.structured_data || data;
    console.log('🔍 AI Citations: Processed data:', processedData);

    // Extract brands and metadata
    this.brands = processedData.brands || [];
    this.metadata = processedData.metadata || data.metadata || null;
    
    // Extract evidence sources from Tavily
    this.evidenceSources = this.transformSourcesData(
      processedData.evidence_sources || 
      data.evidence_sources || 
      []
    );
    
    console.log('🔍 AI Citations: Brands extracted:', this.brands);
    console.log('🔍 AI Citations: Number of brands:', this.brands.length);
    console.log('🔍 AI Citations: Evidence sources:', this.evidenceSources.length);

    // Recalculate smart date range when new data arrives
    this.calculateSmartDateRange();

    // Prepare all data using structured methods
    this.prepareTableData();
    this.updateCompositeScore();
    
    // Check visibility and prepare chart data
    this.checkTabVisibility();
    
    // Only prepare chart data if tab is visible
    if (this.isTabVisible) {
      this.prepareChartData();
    } else {
      console.log('🔍 AI Citations: Tab not visible, deferring chart preparation');
    }

    this.cdr.markForCheck();
  }

  private prepareChartData(): void {
    if (!this.brands || this.brands.length === 0) {
      this.results = [];
      this.lineChart = [];
      return;
    }

    this.prepareComboChartData();
  }

  private prepareComboChartData(): void {
    const brands = this.brands;
    const youBrand = this.findYouBrand(brands);
    const competitorBrands = this.findCompetitorBrands(brands);

    console.log('🔍 AI Citations Chart: All brands:', brands);
    console.log('🔍 AI Citations Chart: You brand:', youBrand);
    console.log('🔍 AI Citations Chart: Competitor brands:', competitorBrands);
    console.log('🔍 AI Citations Chart: Competitor count:', competitorBrands.length);

    // Prepare bar chart data (results) - YOU brand metrics as bars (keep zeros to align with competitors)
    if (youBrand) {
      this.results = [
        { name: 'Citation Frequency', value: youBrand.citation_frequency || 0 },
        { name: 'Citation Share of Voice', value: youBrand.citation_share_of_voice || 0 },
        { name: 'Authority Weight', value: youBrand.authority_weight || 0 },
        { name: 'Relevance Score', value: youBrand.relevance_score || 0 },
        { name: 'AI Readability', value: youBrand.ai_readability || 0 },
        { name: 'External Mentions', value: youBrand.external_mentions || 0 },
        { name: 'Diversity Index', value: youBrand.diversity_index || 0 },
        { name: 'Persistence Score', value: youBrand.persistence_score || 0 }
      ];
    } else {
      this.results = [];
    }

    // Prepare line chart data (lineChart) - competitor brands as lines (keep zeros to ensure visibility)
    if (competitorBrands.length > 0) {
      this.lineChart = competitorBrands.map(brand => {
        const series = [
          { name: 'Citation Frequency', value: brand.citation_frequency || 0 },
          { name: 'Citation Share of Voice', value: brand.citation_share_of_voice || 0 },
          { name: 'Authority Weight', value: brand.authority_weight || 0 },
          { name: 'Relevance Score', value: brand.relevance_score || 0 },
          { name: 'AI Readability', value: brand.ai_readability || 0 },
          { name: 'External Mentions', value: brand.external_mentions || 0 },
          { name: 'Diversity Index', value: brand.diversity_index || 0 },
          { name: 'Persistence Score', value: brand.persistence_score || 0 }
        ];

        return {
          name: brand.name,
          series: series
        };
      });

      console.log('🔍 AI Citations Chart: Line chart data prepared:', this.lineChart);
    } else {
      this.lineChart = [];
      console.log('🔍 AI Citations Chart: No competitor brands found, line chart empty');
    }
  }

  private prepareTableData(): void {
    if (!this.brands || this.brands.length === 0) {
      this.tableRows = [];
      return;
    }

    // Initialize table columns
    this.tableColumns = [
      { key: 'name', header: 'Brand', className: 'brand-column' },
      { key: 'citation_frequency', header: 'Citation Frequency', className: 'metric-column' },
      { key: 'citation_share_of_voice', header: 'Citation Share of Voice', className: 'metric-column' },
      { key: 'authority_weight', header: 'Authority Weight', className: 'metric-column' },
      { key: 'relevance_score', header: 'Relevance Score', className: 'metric-column' },
      { key: 'ai_readability', header: 'AI Readability', className: 'metric-column' },
      { key: 'external_mentions', header: 'External Mentions', className: 'metric-column' },
      { key: 'diversity_index', header: 'Diversity Index', className: 'metric-column' },
      { key: 'persistence_score', header: 'Persistence Score', className: 'metric-column' }
    ];

    // Prepare table rows
    this.tableRows = this.brands.map(brand => ({
      name: brand.name,
      is_your_brand: this.isYouBrand(brand),
      citation_frequency: brand.citation_frequency || 0,
      citation_share_of_voice: brand.citation_share_of_voice || 0,
      authority_weight: brand.authority_weight || 0,
      relevance_score: brand.relevance_score || 0,
      ai_readability: brand.ai_readability || 0,
      external_mentions: brand.external_mentions || 0,
      diversity_index: brand.diversity_index || 0,
      persistence_score: brand.persistence_score || 0
    }));
  }

  private updateCompositeScore(): void {
    if (!this.brands || this.brands.length === 0) {
      this.compositeScore = 0;
      return;
    }

    // Get target brand (you brand or first brand)
    const targetBrand = this.findYouBrand(this.brands) || this.brands[0];

    // Calculate composite score as average of all citation metrics
    const metrics = [
      targetBrand.citation_frequency || 0,
      targetBrand.citation_share_of_voice || 0,
      targetBrand.authority_weight || 0,
      targetBrand.relevance_score || 0,
      targetBrand.ai_readability || 0,
      targetBrand.external_mentions || 0,
      targetBrand.diversity_index || 0,
      targetBrand.persistence_score || 0
    ];

    this.compositeScore = Math.round(metrics.reduce((sum, score) => sum + score, 0) / metrics.length);
  }

  // Helper methods following overview pattern
  private findYouBrand(brands: any[]): any {
    if (!brands || brands.length === 0) return null;
    
    // Look for brand marked as "your brand" or first brand
    const youBrand = brands.find(brand => brand.is_your_brand === true);
    return youBrand || brands[0];
  }

  private findCompetitorBrands(brands: any[]): any[] {
    if (!brands || brands.length <= 1) return [];
    
    // Return all brands except the "you" brand
    const youBrand = this.findYouBrand(brands);
    return brands.filter(brand => brand.name !== youBrand?.name);
  }

  private isYouBrand(brand: any): boolean {
    if (!brand) return false;
    return brand.is_your_brand === true || brand.name === this.brands[0]?.name;
  }


  // Most cited domains data - dynamically generated from API response
  get citedDomainsData(): any[] {
    // First, check for new simplified table data from backend (new name: citations_table)
    // Keep fallback for backward compatibility with old name
    const tableData = this.aiCitationsResponse?.structured_data?.context?.citations_table ||
                      this.aiCitationsResponse?.context?.citations_table ||
                      this.aiCitationsResponse?.structured_data?.context?.cited_domains_table ||
                      this.aiCitationsResponse?.context?.cited_domains_table;
    
    if (tableData && Array.isArray(tableData) && tableData.length > 0) {
      // Use backend-provided table data directly
      return tableData.map((item: any) => ({
        domain: item.domain,
        citation_count: 1, // Each entry represents one domain
        sources: item.category || '',
        authority_score: item.authority_score || 0,
        confidence_avg: (item.authority_score || 0) / 100 // Convert to 0-1 scale
      })).slice(0, 50);
    }

    // Fallback: Get citations from multiple possible locations and process
    // Use getCitations() to get actual Tavily/web search citations for fallback processing
    const citations = this.getCitations();

    if (!citations || citations.length === 0) {
      return [];
    }

    // Extract domains from citations and count occurrences
    const domainCounts = new Map<string, { count: number, sources: string[], urls: string[] }>();

    citations.forEach((citation: any) => {
      const domain = this.extractDomainFromUrl(citation.url);
      if (domain) {
        if (!domainCounts.has(domain)) {
          domainCounts.set(domain, { count: 0, sources: [], urls: [] });
        }
        const domainData = domainCounts.get(domain)!;
        domainData.count++;
        if (!domainData.sources.includes(citation.source)) {
          domainData.sources.push(citation.source);
        }
        domainData.urls.push(citation.url);
      }
    });

    // Convert to array and sort by count
    return Array.from(domainCounts.entries())
      .map(([domain, data]) => ({
        domain: domain,
        citation_count: data.count,
        sources: data.sources.join(', '),
        authority_score: this.calculateAuthorityScore(data.count, data.sources.length),
        confidence_avg: this.calculateAverageConfidence(domain, data.urls)
      }))
      .sort((a, b) => b.citation_count - a.citation_count)
      .slice(0, 50); // limit to top 50 domains
  }

  get filteredCitedDomains(): any[] {
    const data = this.citedDomainsData;
    if (this.selectedCitedDomainCategory === 'All') {
      return data;
    }
    return data.filter(row =>
      (row.sources || '').toLowerCase().includes(this.selectedCitedDomainCategory.toLowerCase())
    );
  }

  onCitedDomainCategoryChange(value: string): void {
    this.selectedCitedDomainCategory = value;
  }

  // Helper method to get citations for domains extraction
  private getCitationsForDomains(): any[] {
    if (!this.aiCitationsResponse) return [];

    // This method is no longer needed since we removed cited_domains
    // Return empty array as fallback (citations_table is used directly in citedDomainsData)
    return [];
  }

  // Extract domain from URL
  private extractDomainFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch (error) {
      return null;
    }
  }

  // Calculate authority score based on citation count and source diversity
  private calculateAuthorityScore(citationCount: number, sourceDiversity: number): number {
    // Base score from citation count (0-100)
    const citationScore = Math.min(citationCount * 10, 100);

    // Bonus for source diversity (0-20 points)
    const diversityBonus = Math.min(sourceDiversity * 5, 20);

    return Math.min(citationScore + diversityBonus, 100);
  }

  // Calculate average confidence for a domain
  private calculateAverageConfidence(domain: string, urls: string[]): number {
    // Use getCitations() for fallback path (when citations_table not available)
    // This gets actual Tavily/web search citations, not the empty getCitationsForDomains()
    const citations = this.getCitations();
    if (!citations || citations.length === 0) return 0;

    const domainCitations = citations.filter(
      (citation: any) => this.extractDomainFromUrl(citation.url) === domain
    );

    if (domainCitations.length === 0) return 0;

    const totalConfidence = domainCitations.reduce((sum: number, citation: any) => sum + (citation.confidence || 0), 0);
    return Math.round((totalConfidence / domainCitations.length) * 100) / 100;
  }

  // Get appropriate chart subtitle
  getChartSubtitle(): string {
    if (!this.brands || this.brands.length === 0) return 'AI Citations analysis';

    const brands = this.brands;
    if (brands.length === 1) {
      const brand = brands[0];
      return `📊 Single Brand Analysis: ${brand.name} - Detailed AI Citations metrics breakdown`;
    } else {
      const youBrand = this.findYouBrand(brands);
      const competitorCount = brands.length - 1;
      if (youBrand) {
        return `AI Citations comparison: ${youBrand.name} (bars) vs ${competitorCount} competitor${competitorCount > 1 ? 's' : ''} (lines)`;
      } else {
        return `AI Citations comparison across ${brands.length} brands`;
      }
    }
  }

  // Update responsive view dimensions
  private updateViewByBreakpoint = () => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    if (width <= 480) {
      this.view = [300, 300];
    } else if (width <= 768) {
      this.view = [400, 350];
    } else if (width <= 1024) {
      this.view = [500, 400];
    } else {
      this.view = [600, 400];
    }
    this.cdr.markForCheck();
  }



  convertToCitations(citations: any[]): any[] {
    if (!citations) return [];
    return citations.map((c: any, index: number) => ({
      id: c.id || index + 1,
      title: c.title || '',
      url: c.url || '',
      snippet: c.snippet || '',
      domain: c.domain || c.source || '',
      source: c.source || c.domain || '',
      confidence: c.confidence || 0.7,
      type: c.type || 'web',
      timestamp: c.timestamp || new Date().toISOString()
    }));
  }

  private transformSourcesData(sources: any[]): any[] {
    return sources.map((source, index) => ({
      title: source.title || source.snippet || `Source ${index + 1}`,
      url: source.url,
      confidence: source.confidence || 0.7,
      snippet: source.snippet || source.title,
      source: source.source || "Tavily",
      category: 'ai_citations',
      id: `source_${index}`
    }));
  }

  getEvidenceSources(): any[] {
    return this.evidenceSources || [];
  }

  // Recommended domains data
  get recommendedDomainsData(): any[] {
    if (!this.aiCitationsResponse) return [];
    
    const recommendedDomains = this.aiCitationsResponse.structured_data?.context?.recommended_domains ||
                               this.aiCitationsResponse.context?.recommended_domains ||
                               [];
    
    // Merge with cited domains to mark which are already cited
    const citedDomains = this.citedDomainsData.map(d => d.domain);
    
    return recommendedDomains.map((domain: any) => ({
      domain: domain.domain || domain,
      relevance_score: domain.relevance_score || 0,
      category: domain.category || 'General',
      is_recommended: true,
      is_cited: citedDomains.includes(domain.domain || domain),
      is_valid: domain.is_valid !== false, // Default to true if not specified
      description: domain.description || ''
    })).sort((a: any, b: any) => b.relevance_score - a.relevance_score);
  }

  // Cited pages data with filters (uses root cited_pages or falls back to LLM comparison cited_pages_list so all domains/sites appear)
  get citedPagesData(): any[] {
    if (!this.aiCitationsResponse) return [];
    
    let citedPages = this.aiCitationsResponse.cited_pages || this.aiCitationsResponse.structured_data?.cited_pages;
    if (!citedPages || (Array.isArray(citedPages) && citedPages.length === 0)) {
      const list = this.firstAiVisibilityMetrics?.cited_pages_list;
      if (Array.isArray(list) && list.length > 0) {
        citedPages = list.map((p: any) => ({
          url: p.url,
          domain: p.domain,
          llm_sources: (p.model_sources || []).reduce((acc: Record<string, number>, src: string) => {
            const key = (src || '').toLowerCase().replace(/\s/g, '_');
            if (key) acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {}),
          volume: p.citation_count ?? '0'
        }));
      } else {
        citedPages = [];
      }
    }
    if (!citedPages) citedPages = [];
    
    // Apply filters
    if (this.citedPagesFilters.selectedLLMSources.length > 0) {
      citedPages = citedPages.filter((page: any) => {
        const llmSources = page.llm_sources || {};
        return this.citedPagesFilters.selectedLLMSources.some(source => {
          const sourceKey = source.toLowerCase();
          return llmSources[sourceKey] > 0;
        });
      });
    }
    
    if (this.citedPagesFilters.selectedDomains.length > 0) {
      citedPages = citedPages.filter((page: any) => this.citedPagesFilters.selectedDomains.includes(page.domain));
    }
    
    return citedPages.map((page: any) => ({
      ...page,
      page: this.formatUrl(page.url),
      page_url: page.url,
      llm_sources: this.formatLLMSources(page.llm_sources || {}),
      volume: page.volume || '0'
    }));
  }

  // Calculate smart date range (max of project creation date and 30 days ago)
  private calculateSmartDateRange(): void {
    const filterData = this.templateCommonService.getCurrentFilterData();
    const projectCreatedAt = filterData?.projectCreatedAt;
    
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    let startDate: Date;
    
    if (projectCreatedAt) {
      const projectDate = new Date(projectCreatedAt);
      // Use the more recent date (later date)
      startDate = projectDate > thirtyDaysAgo ? projectDate : thirtyDaysAgo;
    } else {
      startDate = thirtyDaysAgo;
    }
    
    this.smartDateRange = {
      start: startDate,
      end: today
    };
    
    // Update cited pages filters with smart date range
    this.citedPagesFilters.dateRange = {
      start: startDate,
      end: today
    };
  }

  // Format LLM sources for display
  private formatLLMSources(llmSources: any): string {
    const sources: string[] = [];
    if (llmSources.chatgpt > 0) sources.push(`ChatGPT (${llmSources.chatgpt})`);
    if (llmSources.perplexity > 0) sources.push(`Perplexity (${llmSources.perplexity})`);
    if (llmSources.gemini > 0) sources.push(`Gemini (${llmSources.gemini})`);
    if (llmSources.claude > 0) sources.push(`Claude (${llmSources.claude})`);
    return sources.length > 0 ? sources.join(', ') : 'None';
  }

  // Format URL for display
  private formatUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname.length > 50 
        ? urlObj.pathname.substring(0, 50) + '...' 
        : urlObj.pathname || urlObj.hostname;
    } catch {
      return url.length > 50 ? url.substring(0, 50) + '...' : url;
    }
  }

  // Get available LLM sources for filter
  get availableLLMSources(): string[] {
    const pages = this.aiCitationsResponse?.cited_pages || [];
    const sources = new Set<string>();
    
    pages.forEach((page: any) => {
      const llmSources = page.llm_sources || {};
      if (llmSources.chatgpt > 0) sources.add('ChatGPT');
      if (llmSources.perplexity > 0) sources.add('Perplexity');
      if (llmSources.gemini > 0) sources.add('Gemini');
      if (llmSources.claude > 0) sources.add('Claude');
    });
    
    return Array.from(sources);
  }

  // Get available domains for filter (from both root cited_pages and LLM comparison cited_pages_list so all domains appear)
  get availableDomains(): string[] {
    const domains = new Set<string>();
    const rootPages = this.aiCitationsResponse?.cited_pages || this.aiCitationsResponse?.structured_data?.cited_pages || [];
    rootPages.forEach((page: any) => {
      if (page?.domain) domains.add(page.domain);
    });
    const llmList = this.firstAiVisibilityMetrics?.cited_pages_list;
    if (Array.isArray(llmList)) {
      llmList.forEach((p: any) => {
        if (p?.domain) domains.add(p.domain);
      });
    }
    return Array.from(domains).sort();
  }

  // Filter change handlers
  onLLMSourceFilterChange(sources: string[]): void {
    this.citedPagesFilters.selectedLLMSources = sources;
    this.cdr.markForCheck();
  }

  onDomainFilterChange(domains: string[]): void {
    this.citedPagesFilters.selectedDomains = domains;
    this.cdr.markForCheck();
  }

  // Export cited pages
  exportCitedPages(): void {
    if (!this.isPlatformFilterValid) return;
    const data = this.citedPagesData;
    const csv = this.convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cited-pages-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private convertToCSV(data: any[]): string {
    if (!data || data.length === 0) return '';
    
    const headers = ['Page', 'AI Responses', 'Volume', 'LLM Sources', 'Domain', 'Page Type'];
    const rows = data.map(page => [
      page.url || '',
      page.ai_responses || 0,
      page.volume || '0',
      this.formatLLMSources(page.llm_sources || {}),
      page.domain || '',
      page.page_type || 'other'
    ]);
    
    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  }

  // Check if data exists in history or if response has the new fields structure
  get hasHistoryData(): boolean {
    if (!this.aiCitationsResponse) return false;
    
    // Check if response has the new fields (even if empty)
    const hasCitedPagesField = this.aiCitationsResponse.cited_pages !== undefined || 
                                this.aiCitationsResponse.structured_data?.cited_pages !== undefined;
    const hasRecommendedDomainsField = this.aiCitationsResponse.recommended_domains !== undefined ||
                                       this.aiCitationsResponse.structured_data?.context?.recommended_domains !== undefined;
    
    // If fields exist, check if they have data
    if (hasCitedPagesField || hasRecommendedDomainsField) {
      return this.citedPagesData.length > 0 || 
             this.recommendedDomainsData.length > 0 || 
             this.citedDomainsData.length > 0;
    }
    
    // For old cached responses without new fields, check if we have any citation data
    return this.citedDomainsData.length > 0 || 
           (this.aiCitationsResponse.structured_data?.context?.citations?.length > 0) ||
           (this.aiCitationsResponse.citations?.length > 0);
  }
  
  // Check if this is an old cached response without new fields
  get isOldCachedResponse(): boolean {
    if (!this.aiCitationsResponse) return false;
    const hasNewFields = this.aiCitationsResponse.cited_pages !== undefined || 
                         this.aiCitationsResponse.recommended_domains !== undefined ||
                         this.aiCitationsResponse.structured_data?.cited_pages !== undefined ||
                         this.aiCitationsResponse.structured_data?.context?.recommended_domains !== undefined;
    return !hasNewFields && !!this.aiCitationsResponse.metadata?.from_cache;
  }

  // Clear cited pages filters
  clearCitedPagesFilters(): void {
    this.citedPagesFilters.selectedLLMSources = [];
    this.citedPagesFilters.selectedDomains = [];
    this.cdr.markForCheck();
  }

  // --- Simple AI Cited: from overview.llm_comparison (reuse same structure as Overview tab) ---
  get showPlatformFilter(): boolean {
    return !!this.firstAiVisibilityMetrics?.platform_breakdown;
  }
  private get firstAiVisibilityMetrics(): any {
    const r = this.aiCitationsResponse;
    const llm = r?.llm_comparison || r?.overview?.llm_comparison || r?.structured_data?.llm_comparison
      || this.templateCommonService.getCurrentAnalysisResponse()?.llm_comparison;
    if (!llm) return null;
    const list = llm.comparison_data;
    const first = Array.isArray(list) && list.length > 0 ? list[0] : null;
    return first?.ai_visibility_metrics ?? null;
  }

  get simpleSourceUsageChartData(): any[] {
    if (!this.isPlatformFilterValid) return [];
    const summary = this.filteredDomainSummary;
    if (!Array.isArray(summary) || summary.length === 0) return [];
    return summary
      .map((d: any) => ({ name: d.domain || '', value: Number(d.used_percent) || 0 }))
      .filter((d: any) => d.name)
      .sort((a: any, b: any) => b.value - a.value)
      .slice(0, 15);
  }

  /** Last 7 days as x-axis labels (fallback when API has no domain_usage_time_series). */
  private get last7DaysLabels(): string[] {
    const labels: string[] = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      labels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }
    return labels;
  }

  /** Format API date string (YYYY-MM-DD) to short label (e.g. "Jan 24"). */
  private formatDateLabel(dateStr: string): string {
    if (!dateStr || dateStr.length < 10) return dateStr;
    const y = dateStr.slice(0, 4);
    const m = dateStr.slice(5, 7);
    const day = dateStr.slice(8, 10);
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(day, 10));
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  /**
   * Line chart: x-axis = dates from API (domain_usage_time_series), y-axis = % used, one line per domain.
   * Uses only API data: dates and values come from domain_usage_time_series. Fallback uses domain_summary with frontend dates.
   */
  get simpleSourceUsageLineChartData(): any[] {
    if (!this.isPlatformFilterValid) return [];
    const metrics = this.firstAiVisibilityMetrics;
    const summary = this.filteredDomainSummary;
    const timeSeries = metrics?.domain_usage_time_series;

    // Use time-series from API only: derive dates from API series, no frontend date generation
    if (Array.isArray(timeSeries) && timeSeries.length > 0) {
      const allowedDomains = this.hasActivePlatformFilter
        ? new Set(summary.map((d: any) => d.domain).filter(Boolean))
        : null;
      const tsDomains = timeSeries
        .filter((d: any) => d?.domain && (!allowedDomains || allowedDomains.has(d.domain)))
        .slice(0, 5);
      // Collect unique dates from API (all series), sorted
      const dateSet = new Set<string>();
      tsDomains.forEach((item: any) => {
        (item.series || []).forEach((p: any) => {
          if (p?.date != null) dateSet.add(String(p.date).slice(0, 10));
        });
      });
      const apiDates = Array.from(dateSet).sort();
      return tsDomains.map((item: any) => {
        const byDate = new Map<string, number>();
        (item.series || []).forEach((p: any) => {
          if (p?.date != null && p?.used_percent != null) {
            byDate.set(String(p.date).slice(0, 10), Number(p.used_percent));
          }
        });
        return {
          name: item.domain,
          series: apiDates.map((dateStr) => ({ name: this.formatDateLabel(dateStr), value: byDate.get(dateStr) ?? 0 }))
        };
      });
    }

    // Fallback: use domain_summary, repeat used_percent for each day (top 5 only)
    if (!Array.isArray(summary) || summary.length === 0) return [];
    const days = this.last7DaysLabels;
    const domains = summary
      .map((d: any) => ({ domain: d.domain || '', used_percent: Number(d.used_percent) || 0 }))
      .filter((d: any) => d.domain)
      .sort((a: any, b: any) => b.used_percent - a.used_percent)
      .slice(0, 5);
    return domains.map((d: any) => ({
      name: d.domain,
      series: days.map((dayLabel) => ({ name: dayLabel, value: d.used_percent }))
    }));
  }

  /** Domain type display names - acronyms like UGC in full caps */
  private static DOMAIN_TYPE_LABELS: Record<string, string> = {
    ugc: 'UGC',
    editorial: 'Editorial',
    corporate: 'Corporate',
    reference: 'Reference',
    competitor: 'Competitor',
    institutional: 'Institutional',
    other: 'Other'
  };

  get simpleDomainTypeDonutData(): any[] {
    const summary = this.filteredDomainSummary;
    if (!Array.isArray(summary) || summary.length === 0) return [];
    const byType: Record<string, number> = {};
    summary.forEach((d: any) => {
      const t = (d.domain_type || 'other').toLowerCase();
      byType[t] = (byType[t] || 0) + Number(d.total_citations || 0);
    });
    return Object.entries(byType).map(([key, value]) => ({
      name: GenericAiCitationsComponent.DOMAIN_TYPE_LABELS[key] ?? (key.charAt(0).toUpperCase() + key.slice(1)),
      value
    }));
  }

  get simpleDomainTypeDonutTotal(): number {
    return this.simpleDomainTypeDonutData.reduce((s, d) => s + d.value, 0);
  }

  get simplePlatformMentionsChartData(): any[] {
    const metrics = this.firstAiVisibilityMetrics;
    const pb = metrics?.platform_breakdown;
    if (!pb || typeof pb !== 'object') return [];
    const labels: Record<string, string> = { chatgpt: 'ChatGPT', gemini: 'Gemini', ai_overview: 'AI Overview', ai_mode: 'AI Mode' };
    let entries = Object.entries(pb).map(([key, val]: [string, any]) => ({
      key,
      name: labels[key] || key,
      value: typeof val === 'object' ? (Number(val?.mentions) || 0) : Number(val) || 0
    }));
    if (!this.isPlatformFilterValid) return [];
    if (this.hasActivePlatformFilter) {
      entries = entries.filter((e) => this.activePlatformFilterIds.includes(e.key));
    }
    return entries.map(({ key, ...rest }) => rest).filter(d => d.value > 0 || d.name);
  }

  /** Capitalize domain type for screenshot-style display (Corporate, UGC, Editorial, Reference). */
  private static capitalizeDomainType(raw: string): string {
    const s = (raw || 'other').toLowerCase();
    if (s === 'ugc') return 'UGC';
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  /** Normalize domain for comparison (lowercase, no www, host only). */
  private static normalizeDomainForCompare(domainOrUrl: string): string {
    if (!domainOrUrl || typeof domainOrUrl !== 'string') return '';
    let s = domainOrUrl.trim().toLowerCase();
    try {
      if (s.startsWith('http://') || s.startsWith('https://')) {
        const u = new URL(s);
        s = u.hostname || s;
      }
      if (s.startsWith('www.')) s = s.slice(4);
      return s;
    } catch {
      if (s.startsWith('www.')) s = s.slice(4);
      return s;
    }
  }

  /** Primary brand domain from filter (base_domain), normalized — used to hide own brand from Sources by domain. */
  private get primaryBrandDomainNormalized(): string | null {
    const filterData = this.templateCommonService.getCurrentFilterData();
    const base = filterData?.base_domain;
    if (!base || typeof base !== 'string') return null;
    const normalized = GenericAiCitationsComponent.normalizeDomainForCompare(base);
    return normalized || null;
  }

  get simpleSourceTableData(): any[] {
    const summary = this.filteredDomainSummary;
    if (!Array.isArray(summary) || summary.length === 0) return [];
    const primaryDomain = this.primaryBrandDomainNormalized;
    const summaryExcludingPrimary = primaryDomain
      ? summary.filter((d: any) => GenericAiCitationsComponent.normalizeDomainForCompare(d.domain || '') !== primaryDomain)
      : summary;
    const sorted = [...summaryExcludingPrimary]
      .map((d: any) => ({
        source: d.domain || '',
        domain_type: GenericAiCitationsComponent.capitalizeDomainType(d.domain_type),
        used: Number(d.used_percent) ?? 0,
        avg_citations: Number(d.avg_citations) ?? 0
      }))
      .sort((a: any, b: any) => b.used - a.used);
    return sorted.map((d: any, i: number) => ({
      rank: i + 1,
      ...d,
      domain_type_raw: (d.domain_type || 'other').toLowerCase().replace(/\s+/g, '_'),
      used_display: `${Math.round(d.used)}%`
    }));
  }

  get simpleSourceTableColumns(): TableColumn[] {
    return [
      { key: 'rank', label: '#', sortable: false, align: 'center', width: '5%' },
      { key: 'source', label: 'Source', sortable: true, align: 'left', width: '35%' },
      { key: 'domain_type', label: 'Domain Type', sortable: true, align: 'left', width: '20%' },
      {
        key: 'used_display',
        label: 'Used (%)',
        sortable: true,
        align: 'center',
        width: '15%',
        tooltip: 'Tracks how often this domain was used in AI answers. May exceed 100% if content from this domain was used multiple times within an answer.'
      },
      {
        key: 'avg_citations',
        label: 'Avg. Citations',
        sortable: true,
        align: 'center',
        width: '25%',
        tooltip: "The domain's average number of citations within a chat when used as a source (from this run)."
      }
    ];
  }

  get simpleSourceTableConfig(): TableConfig {
    return { showHeader: true, showCard: true, enableSorting: true, enableHover: true };
  }

  /** True when there is any data to show in the "AI Cited domains and pages" section (hide section when false). */
  get hasSimpleAiCitedData(): boolean {
    return (
      this.simpleSourceUsageLineChartData.length > 0 ||
      this.simpleDomainTypeDonutData.length > 0 ||
      this.simplePlatformMentionsChartData.length > 0 ||
      this.simpleSourceTableData.length > 0 ||
      this.simpleCitedPagesTableData.length > 0
    );
  }

  /** Primary brand name for screenshot-style column label (e.g. "MG UK mentioned"). */
  get primaryBrandNameForDisplay(): string {
    const you = this.findYouBrand(this.brands || []);
    const name = you?.name ?? this.brands?.[0]?.name;
    return (name && String(name).trim()) ? String(name).trim() : 'Brand';
  }

  /** True when current brand is mentioned (on page or in prompts that cited this URL). */
  private isPrimaryBrandMentioned(p: any): boolean {
    if (p?.brand_mentioned_on_page === true) return true;
    const primary = (this.primaryBrandNameForDisplay || '').trim().toLowerCase();
    if (!primary) return false;
    const tm = p?.total_mentions;
    if (!Array.isArray(tm) || tm.length === 0) return false;
    return tm.some((b: string) => typeof b === 'string' && (b || '').trim().toLowerCase() === primary);
  }

  /** Coerce to number; use 0 when value is NaN, null, or undefined (avoids showing "NaN%" in Sites table). */
  private static safeUsedPercent(value: any): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  /** Coerce to number; use 0 when value is NaN, null, or undefined. */
  private static safeAvgCitations(value: any): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  get simpleCitedPagesTableData(): any[] {
    const list = this.filteredCitedPagesList;
    if (!Array.isArray(list) || list.length === 0) return [];
    return list.slice(0, 50).map((p: any) => {
      const brandYes = this.isPrimaryBrandMentioned(p);
      const usedPercent = GenericAiCitationsComponent.safeUsedPercent(p.used_percent);
      const avgCit = GenericAiCitationsComponent.safeAvgCitations(p.avg_citations);
      return {
        url: p.url || '',
        urlClickable: p.url || '',
        domain: p.domain || '',
        query: (p.queries && p.queries.length) ? p.queries.join(' | ') : '',
        url_type: GenericAiCitationsComponent.capitalizeDomainType(p.url_type || 'other'),
        url_type_raw: (p.url_type || 'other').toLowerCase(),
        brand_mentioned: brandYes ? 'Yes' : (p.brand_mentioned_on_page == null ? 'N/A' : 'No'),
        brand_mentioned_on_page: brandYes || p.brand_mentioned_on_page === true,
        mentioned_brands: this.getOtherMentionedBrandsDisplay(p),
        mentioned_brands_list: this.getOtherMentionedBrandsList(p),
        cited_domains_list: Array.isArray(p.cited_domains) ? p.cited_domains : [],
        mentions: this.getCitedCount(p),
        used_total: usedPercent,
        used_display: `${usedPercent}%`,
        avg_citations: avgCit,
        models: Array.isArray(p.model_sources) ? p.model_sources.join(', ') : ''
      };
    });
  }

  /** Cited pages filtered by selected domain (shown in same location as main cited pages table) */
  get citedPagesForSelectedDomain(): any[] {
    if (!this.selectedDomainForPages) return [];
    const list = this.filteredCitedPagesList;
    if (!Array.isArray(list)) return [];
    const filtered = list.filter((p: any) => (p.domain || '') === this.selectedDomainForPages);
    return filtered.map((p: any) => {
      const brandYes = this.isPrimaryBrandMentioned(p);
      const usedPercent = GenericAiCitationsComponent.safeUsedPercent(p.used_percent);
      const avgCit = GenericAiCitationsComponent.safeAvgCitations(p.avg_citations);
      return {
        url: p.url || '',
        urlClickable: p.url || '',
        domain: p.domain || '',
        query: (p.queries && p.queries.length) ? p.queries.join(' | ') : '',
        url_type: GenericAiCitationsComponent.capitalizeDomainType(p.url_type || 'other'),
        url_type_raw: (p.url_type || 'other').toLowerCase(),
        brand_mentioned: brandYes ? 'Yes' : (p.brand_mentioned_on_page == null ? 'N/A' : 'No'),
        brand_mentioned_on_page: brandYes || p.brand_mentioned_on_page === true,
        mentioned_brands: this.getOtherMentionedBrandsDisplay(p),
        mentioned_brands_list: this.getOtherMentionedBrandsList(p),
        cited_domains_list: Array.isArray(p.cited_domains) ? p.cited_domains : [],
        mentions: this.getCitedCount(p),
        used_total: usedPercent,
        used_display: `${usedPercent}%`,
        avg_citations: avgCit,
        models: Array.isArray(p.model_sources) ? p.model_sources.join(', ') : ''
      };
    });
  }

  /** Mentioned brands display: from total_mentions (list of brand names from prompts that cited this URL) */
  getMentionedBrandsDisplay(p: any): string {
    const tm = p?.total_mentions;
    if (Array.isArray(tm) && tm.length > 0 && typeof tm[0] === 'string') return tm.join(', ');
    return '—';
  }

  /** Other brands mentioned (excluding primary): for Mentions column. */
  getOtherMentionedBrandsDisplay(p: any): string {
    const list = this.getOtherMentionedBrandsList(p);
    return list.length > 0 ? list.join(', ') : '—';
  }

  /** Other mentioned brand names as array (for competitor chips in Mentions column). */
  getOtherMentionedBrandsList(p: any): string[] {
    const tm = p?.total_mentions;
    if (!Array.isArray(tm) || tm.length === 0) return [];
    const primary = (this.primaryBrandNameForDisplay || '').trim().toLowerCase();
    return tm.filter((b: string) => typeof b === 'string' && b.trim().toLowerCase() !== primary).map((b: string) => b.trim()).filter(Boolean);
  }

  /** Cited count: citation_count from API, or length of total_mentions (brands mentioned in citing prompts) */
  getCitedCount(p: any): number {
    if (p?.citation_count != null && typeof p.citation_count === 'number') return p.citation_count;
    const tm = p?.total_mentions;
    if (Array.isArray(tm)) return tm.length;
    return 0;
  }

  /** Whether a cited page matches the selected platform filter */
  private pageMatchesPlatformFilter(p: any): boolean {
    if (!this.isPlatformFilterValid) return false;
    if (!this.hasActivePlatformFilter) return true;
    const sources = p?.model_sources;
    if (!Array.isArray(sources) || sources.length === 0) return false;
    const normalized = (id: string) => String(id).toLowerCase().replace(/\s/g, '_');
    const ids = this.activePlatformFilterIds;
    return sources.some((m: string) => ids.includes(normalized(m)));
  }

  /** Cited pages list filtered by selected platforms */
  private get filteredCitedPagesList(): any[] {
    const metrics = this.firstAiVisibilityMetrics;
    const list = metrics?.cited_pages_list;
    if (!Array.isArray(list)) return [];
    if (!this.isPlatformFilterValid) return [];
    return !this.hasActivePlatformFilter ? list : list.filter((p: any) => this.pageMatchesPlatformFilter(p));
  }

  /** Domain summary filtered by selected platforms (domains that appear in filtered cited pages) */
  private get filteredDomainSummary(): any[] {
    const metrics = this.firstAiVisibilityMetrics;
    const summary = metrics?.domain_summary;
    if (!Array.isArray(summary) || summary.length === 0) return [];
    if (!this.isPlatformFilterValid) return [];
    if (!this.hasActivePlatformFilter) return summary;
    const allowedDomains = new Set(this.filteredCitedPagesList.map((p: any) => p.domain).filter(Boolean));
    return summary.filter((d: any) => allowedDomains.has(d.domain));
  }

  onSimpleSourceRowClick(row: any): void {
    if (!this.isPlatformFilterValid) return;
    const domain = row?.source ?? null;
    if (domain) {
      this.selectedDomainForPages = domain;
      // Keep main view; cited pages table below will show filtered list in same location
    }
    this.cdr.markForCheck();
  }

  goBackFromDomainDetail(): void {
    this.aiCitationsView = 'main';
    this.selectedDomainForPages = null;
    this.cdr.markForCheck();
  }

  clearSelectedDomainForPages(): void {
    this.selectedDomainForPages = null;
    this.cdr.markForCheck();
  }

  onCitedPagesFilterChange(_filter: 'all' | 'chatgpt' | 'gemini' | 'ai_overview' | 'ai_mode'): void {
    this.cdr.markForCheck();
  }

  onCancelLLMComparisonMetrics(taskId: string): void {
    this.templateCommonService.cancelLLMComparisonMetricsTask(taskId);
  }

  onReloadLLMComparisonMetrics(): void {
    this.showProgressBelowLoader = false; // Hide progress below loader when reload clicked
    const currentFilterData = this.templateCommonService.getCurrentFilterData();
    if (currentFilterData) {
      const { projectId, projectdataId } = this.templateCommonService.getCurrentProjectContext();
      this.templateCommonService.startLLMComparisonMetricsCalculation(
        currentFilterData,
        projectdataId || undefined,
        projectId || undefined
      );
    }
  }

  private formatCitedPageUrlDisplay(url: string): string {
    if (!url) return '';
    try {
      const u = new URL(url);
      const path = u.pathname || '';
      return path.length > 50 ? path.slice(0, 50) + '…' : path || u.hostname;
    } catch {
      return url.length > 50 ? url.slice(0, 50) + '…' : url;
    }
  }

  get simpleCitedPagesTableColumns(): TableColumn[] {
    const brandLabel = `${this.primaryBrandNameForDisplay} mentioned`;
    return [
      { key: 'url', label: 'URL', sortable: false, align: 'left', width: '28%' },
      { key: 'url_type', label: 'URL Type', sortable: false, align: 'left', width: '10%' },
      { key: 'brand_mentioned', label: brandLabel, sortable: false, align: 'center', width: '10%' },
      { key: 'mentioned_brands', label: 'Mentions', sortable: false, align: 'left', width: '14%' },
      { key: 'mentions', label: 'Cited', sortable: true, align: 'center', width: '6%' },
      {
        key: 'used_total',
        label: 'Used (%)',
        sortable: true,
        align: 'center',
        width: '10%',
        format: 'percent',
        tooltip: 'Tracks how often this URL was used in AI answers. May exceed 100% if content from this URL was used multiple times within an answer.'
      },
      {
        key: 'avg_citations',
        label: 'Avg. Citations',
        sortable: true,
        align: 'center',
        width: '10%',
        tooltip: "The URL's average number of citations within a chat when used as a source (from this run)."
      }
    ];
  }

  get simpleCitedPagesTableConfig(): TableConfig {
    return { showHeader: true, showCard: true, enableSorting: true, enableHover: true };
  }

  /** Export Sources by domain table as CSV (screenshot-style data). */
  exportSourcesTableCSV(): void {
    if (!this.isPlatformFilterValid) return;
    const cols = this.simpleSourceTableColumns;
    const rows = this.simpleSourceTableData;
    if (!rows.length) return;
    const header = cols.map(c => c.label).join(',');
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header, ...rows.map(r => cols.map(c => escape(r[c.key])).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-visibility-sources-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Export Cited pages table as CSV (screenshot-style data). */
  exportCitedPagesTableCSV(): void {
    if (!this.isPlatformFilterValid) return;
    const cols = this.simpleCitedPagesTableColumns;
    const rows = this.simpleCitedPagesTableData;
    if (!rows.length) return;
    const header = cols.map(c => c.label).join(',');
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header, ...rows.map(r => cols.map(c => escape(r[c.key])).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-visibility-cited-pages-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Export Sites for selected domain as CSV (only the filtered sites for this domain). */
  exportCitedPagesForSelectedDomainCSV(): void {
    if (!this.isPlatformFilterValid) return;
    if (!this.selectedDomainForPages) return;
    const cols = this.simpleCitedPagesTableColumns;
    const rows = this.citedPagesForSelectedDomain;
    if (!rows.length) return;
    const header = cols.map(c => c.label).join(',');
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header, ...rows.map(r => cols.map(c => escape(r[c.key])).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeDomain = (this.selectedDomainForPages || 'domain').replace(/[^a-zA-Z0-9.-]/g, '_');
    a.href = url;
    a.download = `ai-visibility-sites-${safeDomain}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Tab navigation (mobile menu) – delegate to parent tabs
  getVisibleTabs(): any[] {
    if (this.parentTabs && typeof (this.parentTabs as any).getVisibleTabs === 'function') {
      return (this.parentTabs as any).getVisibleTabs();
    }
    return [
      { id: 'overview', name: 'Overview', icon: 'dashboard' },
      { id: 'ai-citations', name: 'AI Citations', icon: 'link' },
      { id: 'category-analysis', name: 'Category Analysis', icon: 'category' },
      { id: 'consumer-insights', name: 'Consumer Insights', icon: 'psychology' },
      { id: 'sentiment-tracking', name: 'Sentiment Tracking', icon: 'sentiment_satisfied' },
      { id: 'ai-content-index', name: 'AI Content Index', icon: 'article' },
      { id: 'prompt-research', name: 'Prompt Creation', icon: 'search' },
      { id: 'content-creation', name: 'Content Creation', icon: 'create' },
      { id: 'prompt-tracking', name: 'Prompt Tracking', icon: 'track_changes' }
    ];
  }

  getSubTabs(parentTabId: string): any[] {
    if (this.parentTabs && typeof (this.parentTabs as any).getSubTabs === 'function') {
      return (this.parentTabs as any).getSubTabs(parentTabId);
    }
    return [];
  }

  openTab(tabId: string): void {
    if (this.parentTabs && typeof (this.parentTabs as any).openTab === 'function') {
      (this.parentTabs as any).openTab(tabId);
    }
  }

  openSubTab(parentTabId: string, subTabId: string): void {
    if (this.parentTabs && typeof (this.parentTabs as any).openSubTab === 'function') {
      (this.parentTabs as any).openSubTab(parentTabId, subTabId);
    }
  }
}

